import { useMemo, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_SETTINGS,
  ZERO,
  ZERO_QTY,
  addMoney,
  addQty,
  formatMoney,
  formatQuantity,
  fromUnits,
  hoy,
  newId,
  parseQuantity,
  sumarDias,
  type Centavos,
  type CuentaBancoCordobas,
} from '@abastece/shared';
import {
  dineroDeVenta,
  efectivoDeVenta,
  fiadoDeVenta,
  quedaPorDevolver,
  repartirDevolucion,
  utilidadDeVenta,
  valorDevuelto,
  type FormaPagoVenta,
  type Venta,
} from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { cuentaEnCordobas } from '../lib/bancos.js';
import { sesionAbierta } from '../lib/caja.js';
import { emitir, emitirYVer } from '../lib/emitir.js';
import {
  ESTADOS_VENTA,
  FORMAS_DE_PAGO,
  SIN_FILTROS_VENTAS,
  TIPOS_VENTA,
  filtrosDeVentas,
  ordenarVentas,
  rangoDe,
  resumenVentas,
  tieneDevoluciones,
  ventaCoincide,
  type ContextoVentas,
  type EstadoVenta,
  type FiltroVentas,
  type Rango,
  type TipoVenta,
} from '../lib/historial.js';
import { imprimirTexto } from '../lib/imprimir.js';
import { datosNegocio, enlaceWhatsApp, textoRecibo, type DatosNegocio } from '../lib/recibo.js';
import { usePuede, useSesion } from '../lib/sesion.js';
import { Campo, Interruptor } from '../ui/Campo.js';
import { ElegirCuenta } from '../ui/ElegirCuenta.js';
import { BuscarEnLista, Desplegable, FilaDesplegables, FiltroFechas, FiltrosEnLinea, claveDeForma } from '../ui/FiltrosHistorial.js';
import { Modal } from '../ui/Modal.js';
import { PedirPin, usePinParaAnular } from '../ui/PedirPin.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Chip, Ico, type NombreIcono } from '../ui/Iconos.js';
import { useT, type Clave } from '../i18n/index.js';
import { useNombreProducto } from '../i18n/productos.js';
import { etiquetaUnidad } from '../i18n/dominio.js';
import { Miniatura, MiniaturaIcono } from '../ui/Miniatura.js';
import { pagosEnCola } from './Transferencias.js';

/** How far back «Ventas rápidas» looks. */
const DIAS_RAPIDAS = 30;

/** How many sales show at a time: the latest, and «Ver más» for the rest. */
const LOTE = 100;

export function Ventas() {
  const t = useT();
  const { hoy: soloHoy, desde, rapidas } = useSearch({ from: '/ventas' });
  const hoyEs = hoy();
  const [filtro, setFiltro] = useState<FiltroVentas>(SIN_FILTROS_VENTAS);
  const [cuantos, setCuantos] = useState(LOTE);
  // Opened from «Vendido hoy», the days are today's and not asked; everything else is.
  const rango: Rango = soloHoy ? { desde: hoyEs, hasta: hoyEs } : rangoDe(filtro.periodo, hoyEs, filtro);
  const puestos = filtrosDeVentas(filtro, soloHoy ? { desde: null, hasta: null } : rango);
  const filtrando = filtro.texto.trim() !== '' || puestos.length > 0;
  const cambiar = (cambio: Partial<FiltroVentas>) => {
    setFiltro((f) => ({ ...f, ...cambio }));
    setCuantos(LOTE);
  };

  /*
   * Opened from «Vendido hoy», the list is that figure's sales: today worked out the way the home
   * screen works it out, and the total over the same unvoided sales, so the two agree. Otherwise,
   * with nothing to find, the latest, a batch at a time; with something, every sale in the days
   * asked for (every sale, with no days), held below against the rest of what was asked.
   */
  const leidas = useLiveQuery(
    async () => {
      if (soloHoy) return { ventas: await db.ventas.where('fecha').equals(hoyEs).toArray(), hay: 0 };
      if (!filtrando) {
        const [ventas, hay] = await Promise.all([db.ventas.reverse().limit(cuantos).toArray(), db.ventas.count()]);
        return { ventas, hay };
      }
      const { desde: d, hasta: h } = rango;
      const enLosDias =
        d !== null && h !== null
          ? db.ventas.where('fecha').between(d, h, true, true)
          : d !== null
            ? db.ventas.where('fecha').aboveOrEqual(d)
            : h !== null
              ? db.ventas.where('fecha').belowOrEqual(h)
              : db.ventas.toCollection();
      return { ventas: await enLosDias.toArray(), hay: 0 };
    },
    [soloHoy, hoyEs, filtrando, filtrando ? 0 : cuantos, rango.desde, rango.hasta],
    undefined,
  );
  /** Whose hands, from each sale's own event, as «Ventas rápidas» says it: read only once asked. */
  const deQuien = useLiveQuery(
    async () => {
      if (!filtro.quien) return undefined;
      const eventos = await db.eventos
        .where('type')
        .equals('venta.registrada')
        .filter((e) => e.actorId === filtro.quien)
        .toArray();
      return new Set(eventos.map((e) => (e.payload as { ventaId: string }).ventaId));
    },
    [filtro.quien],
    undefined,
  );
  /** Every sale not voided, whatever the box and the days narrow the list to: the screen's figure. */
  const registradas = useLiveQuery(() => db.ventas.filter((v) => !v.anulada).count(), [], undefined);
  const clientes = useLiveQuery(() => db.clientes.toArray(), [], []);
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const usuarios = useLiveQuery(() => db.usuarios.toArray(), [], []);
  const contexto = useMemo<ContextoVentas>(
    () => ({
      clientes: new Map(clientes.map((c) => [c.id, c.nombre])),
      productos: new Map(productos.map((p) => [p.id, p])),
      deQuien,
    }),
    [clientes, productos, deQuien],
  );
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  /** An id, not the sale: the detail reads it live, so a return shows the moment it is made. */
  const [abierta, setAbierta] = useState<string | null>(null);
  const puedeConfirmar = usePuede('confirmar_transferencia');
  /*
   * Transfers waiting on somebody to check the bank. A queue, not a place, so it is not a tab:
   * it has something in it only some of the time, and then this says so.
   */
  const porConfirmar = useLiveQuery(async () => (await pagosEnCola()).length, [], 0);
  const verReportes = usePuede('ver_reportes');
  /* What went through as a venta rápida lately: no cost, no stock, no margin behind it. */
  const rapidasRecientes = useLiveQuery(
    async () =>
      verReportes
        ? (await db.ventas.where('fecha').aboveOrEqual(sumarDias(hoy(), -DIAS_RAPIDAS)).toArray()).filter(
            (v) => !v.anulada && v.lineas.some((l) => l.productoId === null),
          ).length
        : 0,
    [verReportes],
    0,
  );

  const todas = leidas?.ventas;
  const encontradas = useMemo(
    () =>
      todas ? ordenarVentas(filtrando ? todas.filter((v) => ventaCoincide(v, filtro, rango, contexto)) : todas, filtro.texto) : [],
    // `rango` is worked out afresh each time: its two days are what it is.
    [todas, filtrando, filtro, rango.desde, rango.hasta, contexto],
  );

  if (rapidas) return <VentasRapidas negocio={datosNegocio(config)} />;

  // Nothing said while it is read: «nothing matches» was only «not read yet».
  const leyendo = leidas === undefined || (filtro.quien !== '' && deQuien === undefined);
  const validas = (todas ?? []).filter((v) => !v.anulada);
  const mostradas = soloHoy || !filtrando ? encontradas : encontradas.slice(0, cuantos);
  const hayMas = soloHoy ? false : filtrando ? encontradas.length > cuantos : (leidas?.hay ?? 0) > (todas?.length ?? 0);
  const resumen = resumenVentas(encontradas);
  // A box over nothing at all would only be a question with no answer.
  const conBuscador = filtrando || (leidas !== undefined && (soloHoy ? leidas.ventas.length : leidas.hay) > 0);
  const nombreUsuario = (id: string) => usuarios.find((u) => u.id === id)?.nombre ?? t('ventas.alguien');

  return (
    <Pagina
      titulo={t(soloHoy ? 'inicio.vendidoHoy' : 'ventas.titulo')}
      volverA={desde === 'pos' ? '/pos' : '/'}
      subtitulo={
        soloHoy
          ? t('ventas.registradasHoy', {
              n: validas.length,
              total: formatMoney(validas.reduce((a, v) => a + v.total, 0) as Centavos),
            })
          : t('ventas.registradas', { n: registradas ?? validas.length })
      }
    >
      {puedeConfirmar && porConfirmar > 0 && (
        <Link
          to="/transferencias"
          search={{ desde: 'ventas' }}
          className="tarjeta-accion mb-4 flex items-center gap-3 p-3.5 ring-amber-500/25"
        >
          <Chip icono="transferencias" tinte="ambar" />
          <span className="min-w-0 flex-1 text-sm font-medium text-amber-200">
            {t('inicio.transferenciasPendientes', { n: porConfirmar })}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-amber-300/60" />
        </Link>
      )}

      {verReportes && !soloHoy && rapidasRecientes > 0 && (
        <Link
          to="/ventas"
          search={{ rapidas: true }}
          className="tarjeta-accion mb-4 flex items-center gap-3 p-3.5"
        >
          <Chip icono="rapido" tinte="violeta" />
          <span className="min-w-0 flex-1 text-sm font-medium">
            {t('ventas.rapidasRecientes', { n: rapidasRecientes, dias: DIAS_RAPIDAS })}
          </span>
          <Ico icono="adelante" className="size-5 shrink-0 text-slate-500" />
        </Link>
      )}

      {conBuscador && (
        <FiltrosEnLinea
          buscar={{ texto: filtro.texto, onTexto: (texto) => cambiar({ texto }), placeholder: t('historial.buscarVentas') }}
          hayPuestos={puestos.length > 0}
          onQuitarTodos={() => cambiar({ ...SIN_FILTROS_VENTAS, texto: filtro.texto })}
        >
          {!soloHoy && <FiltroFechas periodo={filtro.periodo} desde={filtro.desde} hasta={filtro.hasta} onCambio={cambiar} />}
          <BuscarEnLista
            clave="clienteId"
            etiqueta={t('historial.cliente')}
            placeholder={t('historial.buscarCliente')}
            opciones={clientes}
            value={filtro.clienteId}
            onChange={(clienteId) => cambiar({ clienteId })}
          />
          <FilaDesplegables>
            <Desplegable
              clave="forma"
              etiqueta={t('historial.forma')}
              value={filtro.forma}
              onChange={(v) => cambiar({ forma: v as FormaPagoVenta | '' })}
              elegido={filtro.forma ? t(claveDeForma(filtro.forma)) : undefined}
            >
              <option value="">{t('historial.todas')}</option>
              {FORMAS_DE_PAGO.map((f) => (
                <option key={f} value={f}>
                  {t(claveDeForma(f))}
                </option>
              ))}
            </Desplegable>
            <Desplegable
              clave="estado"
              etiqueta={t('historial.estado')}
              value={filtro.estado}
              onChange={(v) => cambiar({ estado: v as EstadoVenta })}
              elegido={filtro.estado === 'todas' ? undefined : t(`historial.estado.${filtro.estado}`)}
            >
              {ESTADOS_VENTA.map((e) => (
                <option key={e} value={e}>
                  {t(`historial.estado.${e}`)}
                </option>
              ))}
            </Desplegable>
            <Desplegable
              clave="tipo"
              etiqueta={t('historial.tipo')}
              value={filtro.tipo}
              onChange={(v) => cambiar({ tipo: v as TipoVenta })}
              elegido={filtro.tipo === 'todas' ? undefined : t(`historial.tipo.${filtro.tipo}`)}
            >
              {TIPOS_VENTA.map((tipo) => (
                <option key={tipo} value={tipo}>
                  {t(`historial.tipo.${tipo}`)}
                </option>
              ))}
            </Desplegable>
            <Desplegable
              clave="quien"
              etiqueta={t('historial.quien')}
              value={filtro.quien}
              onChange={(quien) => cambiar({ quien })}
              elegido={filtro.quien ? t('historial.cobro', { nombre: nombreUsuario(filtro.quien) }) : undefined}
            >
              <option value="">{t('historial.todos')}</option>
              {[...usuarios]
                .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nombre}
                  </option>
                ))}
            </Desplegable>
          </FilaDesplegables>
        </FiltrosEnLinea>
      )}

      {filtrando && !leyendo && encontradas.length > 0 && (
        <p className="tabular mb-3 text-sm text-slate-400" data-historial-resumen>
          {t('historial.encontradas', { n: resumen.n, total: formatMoney(resumen.total) })}
        </p>
      )}

      {leyendo ? null : mostradas.length === 0 ? (
        filtrando ? (
          <Vacio mensaje={t('historial.ninguna')} icono="buscar">
            <button type="button" className="btn-secundario" onClick={() => cambiar(SIN_FILTROS_VENTAS)}>
              <Ico icono="reiniciar" />
              {t('historial.quitarBusqueda')}
            </button>
          </Vacio>
        ) : (
          <Vacio mensaje={t(soloHoy ? 'ventas.sinVentasHoy' : 'ventas.sinVentas')} icono="ventas" />
        )
      ) : (
        <ul className="escalonar flex flex-col gap-2" data-guia="ventas-lista">
          {mostradas.map((v) => (
            <li key={v.id}>
              <button
                type="button"
                onClick={() => setAbierta(v.id)}
                data-sin-icono="fila"
                className={`tarjeta-accion flex w-full items-center justify-between gap-4 text-left ${
                  v.anulada ? 'opacity-50' : ''
                }`}
              >
                <div className="min-w-0">
                  <p className="tabular font-medium">{v.ticket}</p>
                  {/* Whose, on a line of its own: what a search by client is checked against. */}
                  {v.clienteId && contexto.clientes.has(v.clienteId) && (
                    <p className="truncate text-sm text-slate-300">{contexto.clientes.get(v.clienteId)}</p>
                  )}
                  <p className="mt-0.5 text-xs text-slate-500">
                    {t('ventas.lineas', { fecha: v.fecha, n: v.lineas.length })}
                    {v.pagos.some((p) => p.estado === 'pendiente') && (
                      <span className="ml-2 whitespace-nowrap text-amber-400">
                        {t('ventas.transferenciaSinConfirmar')}
                      </span>
                    )}
                    {fiadoDeVenta(v) > 0 && !v.anulada && (
                      <span className="ml-2 text-amber-300">{t('ventas.fiado')}</span>
                    )}
                    {tieneDevoluciones(v) && (
                      <span className="ml-2 whitespace-nowrap text-sky-300">{t('ventas.conDevolucion')}</span>
                    )}
                    {v.anulada && <span className="ml-2 text-rose-400">{t('ventas.anulada')}</span>}
                  </p>
                </div>
                <span className="tabular shrink-0 font-semibold">{formatMoney(v.total)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {hayMas && (
        <button type="button" className="btn-secundario mt-4 w-full" onClick={() => setCuantos((n) => n + LOTE)}>
          <Ico icono="desplegar" />
          {t('comun.verMas')}
        </button>
      )}

      {abierta && (
        <DetalleVenta
          ventaId={abierta}
          negocio={datosNegocio(config)}
          onCerrar={() => setAbierta(null)}
        />
      )}
    </Pagina>
  );
}

/**
 * «Ventas rápidas»: every line sold with no product behind it, lately, and who rang it up.
 *
 * A quick sale has no cost, moves no stock and is never checked against the margin, so this is
 * the one place it shows. A C$ 450 bottle rung as «Varios» at C$ 45 balances the corte and only
 * turns up weeks later as a bottle missing from the shelf; here it turns up as the same name,
 * the same cashier, again and again. What sells often is worth making a product of.
 *
 * Who comes from the sale's own event, as the time does in `horaDeVenta`: the till says whose
 * drawer it was, the event says whose hands.
 */
function VentasRapidas({ negocio }: { negocio: DatosNegocio }) {
  const t = useT();
  const puedeVer = usePuede('ver_reportes');
  const puedeCrear = usePuede('editar_productos');
  const [abierta, setAbierta] = useState<string | null>(null);
  const datos = useLiveQuery(
    async () => {
      const ventas = (await db.ventas.where('fecha').aboveOrEqual(sumarDias(hoy(), -DIAS_RAPIDAS)).toArray())
        .filter((v) => v.lineas.some((l) => l.productoId === null))
        .sort((a, b) => b.ticket.localeCompare(a.ticket));
      const faltan = new Set(ventas.map((v) => v.id));
      const quien = new Map<string, string>();
      if (faltan.size > 0) {
        await db.eventos
          .where('type')
          .equals('venta.registrada')
          .reverse()
          .until(() => faltan.size === 0)
          .each((e) => {
            const id = (e.payload as { ventaId?: string }).ventaId;
            if (id && faltan.delete(id)) quien.set(id, e.actorId);
          });
      }
      const usuarios = new Map((await db.usuarios.toArray()).map((u) => [u.id, u.nombre]));
      return { ventas, quien, usuarios };
    },
    [],
    null,
  );

  if (!puedeVer) {
    return (
      <Pagina titulo={t('ventas.rapidasTitulo')} volverA="/ventas">
        <Vacio mensaje={t('comun.esDelDueno')} />
      </Pagina>
    );
  }
  if (!datos) return null;

  const { ventas, quien, usuarios } = datos;
  const lineas = ventas.flatMap((v) =>
    v.lineas.filter((l) => l.productoId === null).map((l, i) => ({ venta: v, linea: l, key: `${v.id}:${i}` })),
  );
  const validas = lineas.filter((x) => !x.venta.anulada);
  const total = validas.reduce((a, x) => a + x.linea.subtotal, 0) as Centavos;
  const nombreDe = (id: string | undefined) => (id ? usuarios.get(id) : undefined) ?? t('ventas.alguien');

  return (
    <Pagina
      titulo={t('ventas.rapidasTitulo')}
      subtitulo={t('ventas.rapidasSubtitulo', {
        n: validas.length,
        dias: DIAS_RAPIDAS,
        total: formatMoney(total),
      })}
      volverA="/ventas"
    >
      <p className="mb-4 text-sm text-slate-400">{t('ventas.rapidasAyuda')}</p>
      {lineas.length === 0 ? (
        <Vacio mensaje={t('ventas.sinRapidas', { dias: DIAS_RAPIDAS })} icono="rapido" />
      ) : (
        <ul className="escalonar flex flex-col gap-2">
          {lineas.map(({ venta, linea, key }) => (
            <li key={key} className={`tarjeta flex flex-col gap-2 ${venta.anulada ? 'opacity-50' : ''}`}>
              <button
                type="button"
                onClick={() => setAbierta(venta.id)}
                data-sin-icono="fila"
                className="tarjeta-abrir flex w-full items-center justify-between gap-4 text-left"
              >
                <div className="min-w-0">
                  <p className="break-words font-medium">
                    {linea.cantidad === fromUnits(1)
                      ? linea.descripcion
                      : `${formatQuantity(linea.cantidad)} × ${linea.descripcion}`}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {t('ventas.rapidaMeta', {
                      fecha: venta.fecha,
                      ticket: venta.ticket,
                      quien: nombreDe(quien.get(venta.id)),
                    })}
                    {linea.autorizadoPor && (
                      <span className="ml-2 text-amber-300">
                        {t('ventas.autorizo', { nombre: nombreDe(linea.autorizadoPor) })}
                      </span>
                    )}
                    {venta.anulada && <span className="ml-2 text-rose-400">{t('ventas.anulada')}</span>}
                  </p>
                </div>
                <span className="tabular shrink-0 font-semibold">{formatMoney(linea.subtotal)}</span>
              </button>
              {puedeCrear && (
                <Link
                  to="/productos/nuevo"
                  search={{ nombre: linea.descripcion }}
                  className="btn-texto self-start gap-1.5 text-sm text-teal-300"
                >
                  <Ico icono="nuevo" className="size-4" />
                  {t('ventas.crearProducto')}
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}

      {abierta && <DetalleVenta ventaId={abierta} negocio={negocio} onCerrar={() => setAbierta(null)} />}
    </Pagina>
  );
}

/**
 * When a sale happened, from its own event. The sale keeps only the day; the event keeps the
 * moment, and a receipt sent again has to say when the sale was, not when it was sent.
 *
 * Newest first: event ids are UUIDv7, so the index runs in the order things happened, and the
 * sale somebody opens is nearly always a recent one — found in a few steps, not after walking
 * every sale the shop ever made.
 */
async function horaDeVenta(ventaId: string): Promise<string | undefined> {
  const evento = await db.eventos
    .where('type')
    .equals('venta.registrada')
    .reverse()
    .filter((e) => (e.payload as { ventaId?: string }).ventaId === ventaId)
    .first();
  return evento?.clientTs;
}

/**
 * A sale, opened: its lines, totals and payments, its receipt again, a return, a void. From the
 * histories here, and since 2026-10-14 from a delivered order's sheet in «Pedidos» («Ver la venta»).
 */
export function DetalleVenta({
  ventaId,
  negocio,
  onCerrar,
}: {
  ventaId: string;
  negocio: DatosNegocio;
  onCerrar: () => void;
}) {
  const venta = useLiveQuery(() => db.ventas.get(ventaId), [ventaId]);
  const hora = useLiveQuery(() => horaDeVenta(ventaId), [ventaId]);
  const cliente = useLiveQuery(
    async () => (venta?.clienteId ? await db.clientes.get(venta.clienteId) : undefined),
    [venta?.clienteId],
  );
  const presentaciones = usePresentaciones();
  if (!venta) return null;
  return (
    <DetalleCargado
      venta={venta}
      recibo={textoRecibo(venta, negocio, {
        ...(hora ? { hora } : {}),
        ...(cliente ? { cliente: { nombre: cliente.nombre } } : {}),
        presentaciones,
      })}
      telefono={cliente?.telefono ?? undefined}
      onCerrar={onCerrar}
    />
  );
}

function DetalleCargado({
  venta,
  recibo,
  telefono,
  onCerrar,
}: {
  venta: Venta;
  recibo: string;
  /** The client's, when the sale was to somebody with one: the receipt goes straight to them. */
  telefono?: string;
  onCerrar: () => void;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const porId = new Map(productos.map((p) => [p.id, p]));
  const presentaciones = usePresentaciones();
  const sesion = useSesion();
  const puedeAnular = usePuede('anular_venta');
  const puedeDevolver = usePuede('devolver');
  const verCostos = usePuede('ver_costos');
  const [devolviendo, setDevolviendo] = useState(false);
  const [anulando, setAnulando] = useState(false);
  const [pidiendoPin, setPidiendoPin] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [reingresar, setReingresar] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /*
   * Once the sale's own till has closed, its cash went to general cash with the corte, so a void
   * hands it back out of the till open on this device — and without one there is nowhere for it
   * to come from. Null for "none"; undefined while still being looked up.
   */
  const turno = useLiveQuery(async () => (await db.sesiones.get(venta.sesionId)) ?? null, [venta.sesionId]);
  const caja = useLiveQuery(async () => (await sesionAbierta()) ?? null, []);
  const efectivo = efectivoDeVenta(venta);
  const deOtraCaja = efectivo > 0 && turno !== undefined && turno?.estado !== 'abierta';
  // A sale from before this shift takes the owner's PIN to void; still being looked up counts.
  const pin = usePinParaAnular(turno?.estado !== 'abierta');

  // In place of the detail rather than over it: one sheet at a time, and closing the return
  // lands back on the sale with what came back already showing.
  if (devolviendo) return <DevolverVenta venta={venta} onCerrar={() => setDevolviendo(false)} />;

  async function anular(autorizadoPor?: string) {
    if (!motivo.trim()) return;
    setError(null);
    try {
      await emitir('venta.anulada', {
        ventaId: venta.id,
        motivo: motivo.trim(),
        reingresarStock: reingresar,
        ...(autorizadoPor ? { autorizadoPor } : {}),
        ...(deOtraCaja && caja ? { sesionId: caja.id } : {}),
      });
    } catch (e) {
      // Thrown — nobody signed in any more, a reason the books cannot keep — it went unsaid, and
      // «Anular» looked as if it did nothing (2026-10-13).
      setError(e instanceof Error ? e.message : t('ventas.noSePudoAnular'));
      return;
    }
    const despues = await db.ventas.get(venta.id);
    if (despues?.anulada) onCerrar();
    else setError(t('ventas.noSePudoAnular'));
  }

  return (
    <Modal titulo={venta.ticket} onCerrar={onCerrar}>
      <ul className="mb-4 flex flex-col divide-y divide-slate-800">
        {venta.lineas.map((l, i) => {
          // The receipt keeps the name the line was sold under; the screen shows the product in
          // its own language, and a venta rápida — no product behind it — exactly as typed.
          const producto = l.productoId ? porId.get(l.productoId) : undefined;
          return (
            <li key={i} className="flex items-center gap-3 py-2.5">
              {producto ? (
                <Miniatura producto={producto} tamano="sm" />
              ) : (
                <MiniaturaIcono icono="rapido" tamano="sm" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{producto ? nombre(producto) : l.descripcion}</p>
                <p className="tabular mt-0.5 text-xs text-slate-500">
                  {formatQuantity(l.cantidad)}
                  {/* The case, when it was a case: «1 Fardo × 480.00», not a bottle at the price of six. */}
                  {l.unidadVentaId && l.factor !== UNA_UNIDAD && presentaciones.has(l.unidadVentaId) && (
                    <> {etiquetaUnidad(presentaciones.get(l.unidadVentaId)!, presentaciones.get(l.unidadVentaId)!)}</>
                  )}{' '}
                  × {formatMoney(l.precioUnitario, { symbol: false })}
                  {(l.devuelta ?? 0) > 0 && (
                    <span className="ml-2 text-sky-300">
                      {t('ventas.devueltas', { n: formatQuantity(l.devuelta!) })}
                    </span>
                  )}
                  {l.precioUnitario !== l.precioLista && (
                    <span className="ml-2 text-amber-400">
                      {t('pos.lista')} {formatMoney(l.precioLista, { symbol: false })}
                      {l.motivoOverride && ` · ${l.motivoOverride}`}
                    </span>
                  )}
                </p>
              </div>
              <span className="tabular shrink-0 text-sm">
                {formatMoney(l.subtotal, { symbol: false })}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="flex flex-col gap-1 border-t border-slate-800 pt-3">
        {venta.descuento > 0 && (
          <Fila
            etiqueta={t('ventas.descuento')}
            valor={`-${formatMoney(venta.descuento, { symbol: false })}`}
          />
        )}
        {(venta.envio ?? 0) > 0 && (
          <Fila etiqueta={t('ventas.envio')} valor={formatMoney(venta.envio!, { symbol: false })} />
        )}
        {venta.redondeo !== 0 && (
          <Fila
            etiqueta={t('ventas.redondeo')}
            valor={formatMoney(venta.redondeo, { symbol: false, signed: true })}
          />
        )}
        <Fila etiqueta={t('comun.total')} valor={formatMoney(venta.total)} fuerte />
        {venta.pagos.map((p, i) => (
          <Fila
            key={i}
            etiqueta={t(p.forma === 'credito' ? 'pago.fiado' : (`pago.${p.forma}` as Clave))}
            valor={`${formatMoney(p.monto, { symbol: false })}${
              p.estado === 'pendiente' ? t('ventas.pendiente') : ''
            }`}
          />
        ))}
        {venta.cambio > 0 && (
          <Fila etiqueta={t('cobro.vuelto')} valor={formatMoney(venta.cambio, { symbol: false })} />
        )}
        {(venta.devuelto ?? 0) > 0 && (
          <Fila etiqueta={t('ventas.devuelto')} valor={`-${formatMoney(venta.devuelto!, { symbol: false })}`} />
        )}
        {verCostos && (
          <Fila
            etiqueta={t('ventas.utilidadBruta')}
            valor={formatMoney(utilidadDeVenta(venta))}
          />
        )}
      </div>

      {venta.anulada && (
        <p className="mt-4 rounded-lg bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
          {t('ventas.motivoAnulacion', { motivo: venta.motivoAnulacion ?? '' })}
        </p>
      )}

      <div className="mt-5 flex flex-col gap-3">
        <div className="flex gap-2">
          <a
            className="btn-secundario min-w-0 flex-1 px-3 text-sm"
            href={enlaceWhatsApp(recibo, telefono)}
            target="_blank"
            rel="noreferrer"
          >
            <Ico icono="whatsapp" />
            WhatsApp
          </a>
          <button type="button" className="btn-secundario min-w-0 flex-1 px-3 text-sm" onClick={() => imprimirTexto(recibo)}>
            <Ico icono="imprimir" />
            {t('comun.imprimir')}
          </button>
        </div>

        {puedeDevolver && !venta.anulada && venta.lineas.some((l) => quedaPorDevolver(l) > 0) && !anulando && (
          <button type="button" className="btn-secundario w-full" onClick={() => setDevolviendo(true)}>
            <Ico icono="devolver" />
            {t('devolucion.devolver')}
          </button>
        )}

        {/* Once part has come back, the rest goes back as a return too: a void would refund the
            returned part a second time. */}
        {puedeAnular && !venta.anulada && !anulando && !tieneDevoluciones(venta) && (
          <button
            type="button"
            className="btn-texto gap-1.5 text-sm text-slate-500"
            data-guia="venta-anular"
            onClick={() => setAnulando(true)}
          >
            <Ico icono="anular" className="size-4" />
            {t('ventas.anularVenta')}
          </button>
        )}

        {anulando && (
          <div className="flex flex-col gap-3 rounded-xl bg-slate-950/40 p-4 ring-1 ring-white/5 ring-inset">
            {deOtraCaja && caja !== undefined && (
              <p className={`text-xs ${caja ? 'text-slate-400' : 'text-amber-300'}`}>
                {t(caja ? 'ventas.anularDeCaja' : 'ventas.anularSinCaja', { monto: formatMoney(efectivo) })}
              </p>
            )}
            <div data-guia="venta-motivo">
              <Campo
                maxLength={200}
                etiqueta={t('comun.motivo')}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                autoFocus
                autoComplete="off"
              />
            </div>
            <Interruptor
              etiqueta={t('ventas.regresaInventario')}
              descripcion={t('ventas.regresaInventarioDesc')}
              checked={reingresar}
              onChange={setReingresar}
            />
            {error && <p className="text-sm text-rose-400">{error}</p>}
            <div className="flex gap-3">
              <button type="button" className="btn-secundario flex-1" onClick={() => setAnulando(false)}>
                <Ico icono="cancelar" />
                {t('comun.cancelar')}
              </button>
              <button
                type="button"
                className="btn-primario flex-1"
                data-guia="venta-confirmar-anular"
                disabled={!motivo.trim() || (deOtraCaja && !caja)}
                onClick={() => setPidiendoPin(true)}
              >
                <Ico icono="anular" />
                {t('comun.anular')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/*
        A void goes on record under whoever is signed in, so it asks for their PIN first — or for
        the owner's, where the shop has asked for that (see `usePinParaAnular`).
      */}
      {pidiendoPin && (
        <PedirPin
          titulo={pin.titulo}
          motivo={t(
            pin.porTurno ? 'ventas.duenoOtroTurno' : pin.delDueno ? 'ventas.duenoParaAnular' : 'ventas.pinParaAnular',
            { ticket: venta.ticket, nombre: sesion?.nombre ?? '' },
          )}
          verificar={pin.verificar}
          donde={pin.donde}
          onCancelar={() => setPidiendoPin(false)}
          onAutorizado={(usuarioId) => {
            setPidiendoPin(false);
            void anular(pin.delDueno ? usuarioId : undefined);
          }}
        />
      )}
    </Modal>
  );
}

function Fila({ etiqueta, valor, fuerte }: { etiqueta: string; valor: string; fuerte?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className={`text-sm ${fuerte ? 'font-medium' : 'capitalize text-slate-400'}`}>{etiqueta}</span>
      <span className={`tabular ${fuerte ? 'text-lg font-semibold' : 'text-sm'}`}>{valor}</span>
    </div>
  );
}

type Reembolso = 'efectivo_cordobas' | 'credito' | 'transferencia' | 'billetera';

const REEMBOLSOS: { forma: Reembolso; etiqueta: Clave; icono: NombreIcono }[] = [
  { forma: 'efectivo_cordobas', etiqueta: 'pago.efectivo_cordobas', icono: 'caja' },
  { forma: 'credito', etiqueta: 'devolucion.aSuCuenta', icono: 'fiado' },
  { forma: 'transferencia', etiqueta: 'pago.transferencia', icono: 'transferencias' },
  { forma: 'billetera', etiqueta: 'pago.billetera', icono: 'billetera' },
];

/**
 * Part of a sale coming back: one bottle of three, a sack that turned out torn.
 *
 * Only what can still come back is offered, and what it is worth comes from the function the
 * reducer books it with, so the amount the customer is told is the amount that leaves. The
 * money goes back the way that fits the sale: off the account of a client who bought on fiado,
 * out of the drawer when this device has one open, or by transfer.
 */
function DevolverVenta({ venta, onCerrar }: { venta: Venta; onCerrar: () => void }) {
  const t = useT();
  const nombre = useNombreProducto();
  const sesion = useSesion();
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const porId = new Map(productos.map((p) => [p.id, p]));
  const caja = useLiveQuery(() => sesionAbierta(), [], undefined);
  const cliente = useLiveQuery(
    async () => (venta.clienteId ? await db.clientes.get(venta.clienteId) : undefined),
    [venta.clienteId],
  );
  const [cantidades, setCantidades] = useState<Record<number, string>>({});
  const [reingresar, setReingresar] = useState(true);
  const [elegida, setElegida] = useState<Reembolso | null>(null);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  /** The córdoba account a refund by transfer goes out of, once picked. */
  const [cuentaElegida, setCuentaElegida] = useState<CuentaBancoCordobas | null>(null);
  const cuenta = cuentaEnCordobas(config, cuentaElegida);
  const [motivo, setMotivo] = useState('');
  const [pidiendoPin, setPidiendoPin] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Money back on a sale from before this shift takes the owner's PIN, as a void does.
  const turno = useLiveQuery(async () => (await db.sesiones.get(venta.sesionId)) ?? null, [venta.sesionId]);
  const pin = usePinParaAnular(turno?.estado !== 'abierta');

  /**
   * Money goes back the way it came, as the books take it (`devoluciones.ts`): as money, never more
   * than the sale took in as money and has not given back already; fiado, onto the account; and a
   * transfer still in the queue is not money in hand yet.
   */
  const yaEnDinero = useLiveQuery(
    async () =>
      (await db.devoluciones.where('ventaId').equals(venta.id).toArray())
        .filter((d) => d.reembolso !== 'credito')
        .reduce((s, d) => s + d.total, 0),
    [venta.id],
    0,
  );
  const quedaEnDinero = Math.max(0, dineroDeVenta(venta) - yaEnDinero) as Centavos;
  const formas = REEMBOLSOS.filter(
    (f) =>
      (f.forma !== 'efectivo_cordobas' || caja !== undefined) &&
      (f.forma !== 'credito' || venta.clienteId !== null) &&
      (f.forma === 'credito' || quedaEnDinero > 0),
  );
  // Fiado comes back off the account it went on; anything else out of the drawer, if there is one.
  const porDefecto: Reembolso | null =
    venta.clienteId !== null && (fiadoDeVenta(venta) > 0 || quedaEnDinero === 0)
      ? 'credito'
      : caja !== undefined && quedaEnDinero > 0
        ? 'efectivo_cordobas'
        : null;
  const reembolso = elegida !== null && formas.some((f) => f.forma === elegida) ? elegida : porDefecto;

  const pedidas = venta.lineas.map((l, indice) => {
    const texto = (cantidades[indice] ?? '').trim();
    const cantidad = texto === '' ? ZERO_QTY : parseQuantity(texto);
    const queda = quedaPorDevolver(l);
    return {
      indice,
      cantidad: cantidad ?? ZERO_QTY,
      queda,
      valida: cantidad !== null && cantidad >= 0 && cantidad <= queda,
    };
  });
  const aDevolver = pedidas.filter((p) => p.valida && p.cantidad > 0);
  const total = aDevolver.reduce<Centavos>((suma, p) => {
    const desde = venta.lineas[p.indice]!.devuelta ?? ZERO_QTY;
    return addMoney(suma, valorDevuelto(venta, p.indice, desde, addQty(desde, p.cantidad)).neto);
  }, ZERO);
  /** Which way it goes back, as the books will take it: against a transfer that never came, or not yet. */
  const completa = venta.lineas.every((l, i) => addQty(l.devuelta ?? ZERO_QTY, pedidas[i]!.cantidad) >= l.cantidad);
  const reparto = reembolso !== null ? repartirDevolucion(venta, yaEnDinero, total, reembolso, completa) : null;
  /** More than can go back as money: said, and the rest goes onto the account. */
  const pasaDelDinero = reparto !== null && reembolso !== 'credito' && reparto.falta > 0;
  /** Paid with a transfer still in the queue: it goes onto the account once it is seen at the bank. */
  const esperaLaTransferencia = reparto !== null && reembolso === 'credito' && reparto.falta > 0;
  /** What the customer gets back, the way picked: none of what goes against a transfer that never came. */
  const vuelve = reparto?.devuelto ?? total;
  const listo =
    aDevolver.length > 0 &&
    pedidas.every((p) => p.valida) &&
    reembolso !== null &&
    !pasaDelDinero &&
    !esperaLaTransferencia &&
    motivo.trim() !== '' &&
    !ocupado;

  async function registrar(autorizadoPor?: string) {
    if (!listo || reembolso === null) return;
    setOcupado(true);
    setError(null);
    const devolucionId = newId();
    try {
      const { rechazo } = await emitirYVer('devolucion.registrada', {
        devolucionId,
        ventaId: venta.id,
        fecha: hoy(),
        ...(reembolso === 'efectivo_cordobas' && caja ? { sesionId: caja.id } : {}),
        lineas: aDevolver.map((p) => ({ indice: p.indice, cantidad: p.cantidad, reingresar })),
        reembolso,
        ...(reembolso === 'transferencia' ? { cuenta } : {}),
        motivo: motivo.trim(),
        ...(autorizadoPor ? { autorizadoPor } : {}),
      });
      if (!rechazo && (await db.devoluciones.get(devolucionId))) {
        onCerrar();
        return;
      }
      setError(rechazo ?? t('devolucion.noSePudo'));
    } catch (e) {
      // Thrown, it used to leave the button on «Guardando» for good.
      setError(e instanceof Error ? e.message : t('devolucion.noSePudo'));
    }
    setOcupado(false);
  }

  const explicacion =
    reembolso === 'efectivo_cordobas'
      ? t('devolucion.deLaGaveta')
      : reembolso === 'credito'
        ? t('devolucion.deSuCuenta', { nombre: cliente?.nombre ?? '' })
        : reembolso === 'transferencia'
          ? t('devolucion.delBanco')
          : reembolso === 'billetera'
            ? t('devolucion.deLaBilletera')
            : null;

  return (
    <Modal titulo={t('devolucion.titulo', { ticket: venta.ticket })} onCerrar={onCerrar}>
      <div className="flex flex-col gap-5">
        <p className="text-sm text-slate-400">{t('devolucion.explicacion')}</p>

        <ul className="flex flex-col divide-y divide-slate-800">
          {venta.lineas.map((l, i) => {
            const producto = l.productoId ? porId.get(l.productoId) : undefined;
            const p = pedidas[i]!;
            if (p.queda <= 0) return null;
            return (
              <li key={i} className="flex flex-col gap-2 py-3">
                <div className="flex items-center gap-3">
                  {producto ? (
                    <Miniatura producto={producto} tamano="sm" />
                  ) : (
                    <MiniaturaIcono icono="rapido" tamano="sm" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{producto ? nombre(producto) : l.descripcion}</p>
                    <p className="tabular mt-0.5 text-xs text-slate-500">
                      {t('devolucion.vendido', { n: formatQuantity(l.cantidad) })}
                      {(l.devuelta ?? 0) > 0 &&
                        ` · ${t('devolucion.yaDevuelto', { n: formatQuantity(l.devuelta!) })}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 pl-11">
                  <Campo
                    etiqueta={t('devolucion.cuanto')}
                    inputMode="decimal"
                    className="tabular"
                    value={cantidades[i] ?? ''}
                    placeholder="0"
                    onChange={(e) => setCantidades({ ...cantidades, [i]: e.target.value })}
                    onFocus={(e) => e.currentTarget.select()}
                    error={p.valida ? undefined : t('devolucion.masDeLoQueQueda', { n: formatQuantity(p.queda) })}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="btn-secundario mt-6 shrink-0 px-3 text-sm"
                    onClick={() => setCantidades({ ...cantidades, [i]: formatQuantity(p.queda) })}
                  >
                    <Ico icono="marca" className="size-4" />
                    {t('devolucion.todo')}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>

        <Interruptor
          etiqueta={t('ventas.regresaInventario')}
          descripcion={t('ventas.regresaInventarioDesc')}
          checked={reingresar}
          onChange={setReingresar}
        />

        <div>
          <span className="etiqueta">{t('devolucion.como')}</span>
          <div className="grid grid-cols-2 gap-2">
            {formas.map((f) => (
              <button
                key={f.forma}
                type="button"
                onClick={() => setElegida(f.forma)}
                className={`flex min-h-12 items-center justify-center gap-2 rounded-xl px-2 py-1.5 text-sm font-medium transition ${
                  reembolso === f.forma ? 'bg-marca-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-slate-100'
                }`}
              >
                <Ico icono={f.icono} className="size-5 shrink-0" />
                <span className="text-center leading-tight">{t(f.etiqueta)}</span>
              </button>
            ))}
          </div>
          {explicacion && <p className="mt-2 text-xs text-slate-400">{explicacion}</p>}
          {reparto !== null && reparto.aPerdida > 0 && (
            <p className="mt-2 text-xs text-slate-400" data-devolucion-contra-la-perdida>
              {t('devolucion.contraLaPerdida', { monto: formatMoney(reparto.aPerdida) })}
            </p>
          )}
          {reparto !== null && reparto.aRedondeo > 0 && (
            <p className="mt-2 text-xs text-slate-400">
              {t('devolucion.conElRedondeo', { monto: formatMoney(reparto.devuelto), redondeo: formatMoney(reparto.aRedondeo) })}
            </p>
          )}
          {esperaLaTransferencia && (
            <p className="mt-2 text-xs text-amber-300" data-devolucion-espera>
              {t('devolucion.esperaLaTransferencia')}
            </p>
          )}
          {(pasaDelDinero || (quedaEnDinero === 0 && venta.clienteId === null)) && (
            <p className="mt-2 text-xs text-amber-300">
              {quedaEnDinero > 0
                ? t('devolucion.hastaEnDinero', { monto: formatMoney(quedaEnDinero) })
                : t('devolucion.sinDinero')}
            </p>
          )}
          {reembolso === 'transferencia' && (
            <div className="mt-3">
              <ElegirCuenta valor={cuenta} onCambiar={(c) => setCuentaElegida(c as CuentaBancoCordobas)} etiqueta={t('bancos.pagaDe')} />
            </div>
          )}
          {caja === undefined && <p className="mt-1 text-xs text-slate-500">{t('devolucion.sinCaja')}</p>}
        </div>

        <Campo
          maxLength={200}
          etiqueta={t('comun.motivo')}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          autoComplete="off"
        />

        {error && <p className="text-sm text-rose-400">{error}</p>}

        {/* One button, full width, like the abono: the sheet's ✕ is the way out without doing it. */}
        <button type="button" className="btn-primario" disabled={!listo} onClick={() => setPidiendoPin(true)}>
          <Ico icono="devolver" />
          {vuelve > 0 ? t('devolucion.devolverMonto', { monto: formatMoney(vuelve) }) : t('devolucion.devolver')}
        </button>
      </div>

      {/* Money out of the shop, under whoever is signed in: a PIN first, the same one a void asks for. */}
      {pidiendoPin && (
        <PedirPin
          titulo={pin.titulo}
          motivo={t(
            pin.porTurno ? 'devolucion.pinDuenoOtroTurno' : pin.delDueno ? 'devolucion.pinDueno' : 'devolucion.pin',
            { monto: formatMoney(vuelve), ticket: venta.ticket, nombre: sesion?.nombre ?? '' },
          )}
          verificar={pin.verificar}
          donde={pin.donde}
          onCancelar={() => setPidiendoPin(false)}
          onAutorizado={(usuarioId) => {
            setPidiendoPin(false);
            void registrar(pin.delDueno ? usuarioId : undefined);
          }}
        />
      )}
    </Modal>
  );
}

/** A line of one base unit each: the bottle, the pound. Anything else is a presentation. */
const UNA_UNIDAD = fromUnits(1);

/** Every presentation's name, by id: what a line of a case says it was. */
function usePresentaciones(): ReadonlyMap<string, string> {
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  return useMemo(() => new Map(unidades.map((u) => [u.id, u.nombre])), [unidades]);
}
